// pages/sections/QuotedFeeModal.js
// The "Quote Fee" pop-up for one prior-year job. Opens on the suggested quote — the greater of
// 107% of PY and the office-realization figure, rounded up to the next $100 (computed by the API
// as row.suggested) — or on the last submitted amount when re-quoting. The partner submits it as
// is or changes it. The expected tech fee (Steering's Tech Fee Calc, row.techFee) is added ON TOP of what the
// partner types: the suggested figure is a total, so the input opens on suggested - tech fee, and the total
// (input + tech fee) is what goes to Knuula. An amount under the suggested input is flagged, not blocked.
// Submitting is optimistic: the section closes this modal and marks the row done at
// once, then writes the figure to Knuula in the background and reverts the row (with a toast) if Knuula
// does not confirm it.

import React, { useState } from 'react';
import Modal from '../../components/Modal';
import { formatMoney } from './clientDataUtil';

const FEE_LABEL = { tax: 'Tax', aa: 'Audit & Assurance' };
const money = n => (n == null ? '—' : formatMoney(n, { cents: true }));
const round2 = n => Math.round(n * 100) / 100;

// "$4,200", "4200.5", " 4,200 " → number, or NaN.
function parseAmount(text) {
  const cleaned = String(text ?? '').replace(/[$,\s]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
}

export default function QuotedFeeModal({ row, onClose, onSubmit }) {
  const tech = row.techFee || 0;
  // The input is the total less the tech fee, never below $0 (the total still goes through).
  const suggestedInput = row.suggested == null ? null : Math.max(0, round2(row.suggested - tech));
  const start = row.quote?.amount != null ? Math.max(0, round2(row.quote.amount - tech)) : suggestedInput;
  const [text, setText]     = useState(start == null ? '' : String(start));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const amount = parseAmount(text);
  const total  = Number.isFinite(amount) ? round2(amount + tech) : NaN;
  // $0 is fine when the tech fee alone makes a positive total.
  const valid  = Number.isFinite(amount) && amount >= 0 && round2(amount) === amount && total > 0;
  const under  = valid && suggestedInput != null && amount < suggestedInput ? round2(suggestedInput - amount) : 0;
  const feeName = FEE_LABEL[row.feeType] || row.jobType;
  const driver  = (row.billAt107 ?? 0) >= (row.officeStandard ?? 0) ? '107% of PY' : 'Office Realization';

  async function submit(e) {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(total, tech);     // the section closes the modal; failures surface as a toast there
    } catch (err) {
      setError(err.message || 'The fee could not be submitted.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={saving ? undefined : onClose}
      title={`Quote Fee — ${row.clientCode} ${row.clientName || ''}`}
      footer={(
        <>
          <button type="button" className="rt-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="qf-form" className="rt-btn rt-btn--primary" disabled={!valid || saving}>
            {saving ? 'Submitting…' : (row.quote?.submitted ? 'Update Quote' : 'Submit Quote')}
          </button>
        </>
      )}
    >
      <form id="qf-form" onSubmit={submit}>
        <p className="qf-modal-job">{row.jobName} <span className="qf-modal-type">{feeName}</span></p>

        <table className="qf-figures">
          <tbody>
            <tr><td>107% of PY</td><td className="qf-num">{money(row.billAt107)}</td></tr>
            <tr><td>Office Realization</td><td className="qf-num">{money(row.officeStandard)}</td></tr>
            <tr className={tech > 0 ? '' : 'qf-figures-sum'}>
              <td>Suggested total{row.suggested != null ? ` (greater of the two, from ${driver})` : ''}</td>
              <td className="qf-num">{money(row.suggested)}</td>
            </tr>
            {tech > 0 && (
              <>
                <tr><td>Less expected tech fee (added separately)</td><td className="qf-num">− {money(tech)}</td></tr>
                <tr className="qf-figures-sum">
                  <td>Suggested {feeName} fee to enter</td>
                  <td className="qf-num">{money(suggestedInput)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>

        <label className="qf-label" htmlFor="qf-amount">Quoted {feeName} fee</label>
        <div className={`qf-input-wrap${under > 0 ? ' qf-input-wrap--under' : ''}`}>
          <span className="qf-dollar">$</span>
          <input
            id="qf-amount"
            className="qf-input"
            inputMode="decimal"
            autoFocus
            autoComplete="off"
            value={text}
            onChange={e => setText(e.target.value)}
            disabled={saving}
            aria-invalid={under > 0 || undefined}
          />
        </div>
        {under > 0 && (
          <span className="qf-under-badge" role="status">
            <i className="fas fa-exclamation-circle" /> {money(under)} under the suggested quote
          </span>
        )}
        {text !== '' && !valid && (
          <p className="qf-warn">
            {tech > 0 ? 'Enter a dollar amount of $0 or more (cents allowed).' : 'Enter a dollar amount greater than $0 (cents allowed).'}
          </p>
        )}

        {tech > 0 && valid && (
          <table className="qf-figures qf-total">
            <tbody>
              <tr><td>Your {feeName} fee</td><td className="qf-num">{money(amount)}</td></tr>
              <tr><td>+ Expected tech fee</td><td className="qf-num">{money(tech)}</td></tr>
              <tr className="qf-figures-sum">
                <td>Total sent to Knuula</td>
                <td className="qf-num">{money(total)}</td>
              </tr>
            </tbody>
          </table>
        )}

        <p className="qf-note">
          This sets the <b>{feeName}</b> fee on {row.clientCode}&apos;s Knuula record{tech > 0 && valid ? <> to the <b>total of {money(total)}</b></> : ''}. Knuula keeps one {feeName} fee
          per client, so it replaces whatever is there now: the client&apos;s other jobs, and a low–high range if Knuula
          holds one (it becomes this single figure).
        </p>
        {error && <p className="qf-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
